/**
 * cfGroup — utilities for rendering FilterGroup data.
 *
 * Public functions:
 *  cfGroupStats     — recursive leaf/nesting counts for the "N conditions, M nested" chip
 *  readsAsPredicate — the localized predicate of a tree's "Reads as" sentence, and of
 *                     every other surface that states a group in the reader's words
 *
 * cfGroupStats is hardened (wrapped in try/catch) so a stale database entry never
 * throws and tears down the whole popover.
 */

import type { FilterExpression, FilterGroup, FilterOperator } from 'types/filters';
import { isPropertyLeaf } from '@center-platform/spheres/filters';
import { isFilterExpressionComplete, readBetweenBounds, type TranslateFn } from './filterOperators';
import {
  relativeDayCount,
  summarizeArcFilter,
  summarizeFilterValue,
  summarizePerspectiveFilter,
  summarizeRelationshipFilter,
} from './filterLaneSummaries';
import { getLaneForLeaf, type LaneLeaf } from './filterGroupLeafLane';
import type { PropertyOption } from '../components/molecules/PropertyFilter/PropertyFilterMolecule';
import type { RelationshipOption } from '../components/molecules/RelationshipFilter/RelationshipFilterMolecule';
import type { PerspectiveOption } from '../components/molecules/PerspectiveFilter/PerspectiveFilterMolecule';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GroupStats {
  conditions: number;
  nested: number;
}

// ── cfGroupStats ──────────────────────────────────────────────────────────────

function countGroupInner(group: FilterGroup): GroupStats {
  const conditions = (group.expressions ?? []).length;
  const nested = (group.groups ?? []).length;
  const childStats = (group.groups ?? []).reduce<GroupStats>(
    (acc, g) => {
      const s = countGroupInner(g);
      return { conditions: acc.conditions + s.conditions, nested: acc.nested + s.nested };
    },
    { conditions: 0, nested: 0 }
  );
  return {
    conditions: conditions + childStats.conditions,
    nested: nested + childStats.nested,
  };
}

/**
 * Count leaves and nested groups recursively.
 * "4 conditions, 1 nested" → { conditions: 4, nested: 1 }
 */
export function cfGroupStats(group: FilterGroup): GroupStats {
  try {
    return countGroupInner(group);
  } catch {
    return { conditions: 0, nested: 0 };
  }
}

const MAX_EXPR_DEPTH = 8;

// ── readsAsPredicate ──────────────────────────────────────────────────────────

/** What the "Reads as" sentence resolves names and words through. */
export interface ReadsAsContext {
  t: TranslateFn;
  /** The aspect catalog — a property leaf reads as its label, never its key. */
  availableProperties?: PropertyOption[];
  availableRelationships?: RelationshipOption[];
  availablePerspectives?: PerspectiveOption[];
  /** The author's locale, for date values. */
  locale?: string;
  /**
   * The host's label for a property the catalog does not list. A column popover's
   * catalog holds only its own column, yet its folded inherited line words the
   * context's and view's clauses on every other property; without this they would
   * all read as "unknown property". Returns the key itself when the host has no
   * better label, which reads as unknown too.
   */
  getPropertyLabel?: (propertyKey: string) => string;
}

/**
 * The clause key per operator (`filters.tree.readsAs.clause.*`). Each clause is
 * a whole template — property, operator words and value — so a locale orders
 * them as its own grammar needs ("7 derniers jours", not "derniers 7 jours").
 */
const CLAUSE_KEYS: Record<FilterOperator, string> = {
  equals: 'equals',
  not_equals: 'notEquals',
  contains: 'contains',
  not_contains: 'notContains',
  starts_with: 'startsWith',
  ends_with: 'endsWith',
  in: 'in',
  not_in: 'notIn',
  contains_all: 'containsAll',
  greater_than: 'greaterThan',
  less_than: 'lessThan',
  greater_than_equals: 'greaterThanEquals',
  less_than_equals: 'lessThanEquals',
  between: 'between',
  within_last_days: 'withinLastDays',
  within_next_days: 'withinNextDays',
  date_within_days: 'dateWithinDays',
  last_7_days: 'last7Days',
  last_30_days: 'last30Days',
  is_empty: 'isEmpty',
  is_not_empty: 'isNotEmpty',
  is_true: 'isTrue',
  is_false: 'isFalse',
  exists: 'exists',
  not_exists: 'notExists',
};

/**
 * A date compares by day, so its equals / less / greater read "is on" / "is before" /
 * "is after", and the inclusive pair reads "is on or before" / "is on or after" —
 * the numeric wording ("is at most 2026-01-31") states a magnitude a reader does not
 * hold about a date.
 */
const DATE_CLAUSE_KEYS: Partial<Record<FilterOperator, string>> = {
  equals: 'on',
  less_than: 'before',
  greater_than: 'after',
  less_than_equals: 'onOrBefore',
  greater_than_equals: 'onOrAfter',
};

/** Operators whose clause takes a day count (and pluralises on it) rather than a value. */
const DAY_COUNT_OPERATORS = new Set<FilterOperator>(['within_last_days', 'within_next_days', 'date_within_days']);

interface ReadsAsPart {
  text: string;
  /** Two or more clauses joined by an operator — bracketed when it sits inside another. */
  compound: boolean;
}

function propertyClause(leaf: FilterExpression, ctx: ReadsAsContext): string | null {
  // An incomplete row is marked in the editor but constrains nothing, so the
  // sentence leaves it out — the same rule the persisted predicate follows.
  if (!isFilterExpressionComplete(leaf)) return null;
  const { t } = ctx;
  const prop = ctx.availableProperties?.find((p) => p.key === leaf.property);
  const hostLabel = ctx.getPropertyLabel?.(leaf.property);
  const property =
    prop?.label ??
    (hostLabel && hostLabel !== leaf.property
      ? hostLabel
      : t('filters.tree.readsAs.unknownProperty', { property: leaf.property }));
  const isDate = prop?.type === 'date' || prop?.type === 'datetime';
  const clauseKey = (isDate && DATE_CLAUSE_KEYS[leaf.operator]) || CLAUSE_KEYS[leaf.operator];
  // `leaf.operator` is typed as `FilterOperator`, but a row can carry a stale/legacy
  // operator at runtime (persisted before validation tightened) that this map — like
  // `isFilterExpressionComplete` above — has no entry for. The sentence leaves it out
  // rather than render `filters.tree.readsAs.clause.undefined` to the user.
  if (!clauseKey) return null;
  const key = `filters.tree.readsAs.clause.${clauseKey}`;
  if (DAY_COUNT_OPERATORS.has(leaf.operator)) {
    const count = relativeDayCount(leaf);
    return count === undefined ? null : t(key, { property, count });
  }
  if (leaf.operator === 'between') {
    // Each bound reads exactly as the row's own read-back formats a single
    // value (dates in the author's locale); the clause places "and" between.
    const { min, max } = readBetweenBounds(leaf);
    const bound = (value: number | string | undefined) =>
      summarizeFilterValue({ ...leaf, operator: 'equals', min: undefined, max: undefined, value }, t, prop, ctx, ctx.locale);
    return t(key, { property, min: bound(min), max: bound(max) });
  }
  return t(key, { property, value: summarizeFilterValue(leaf, t, prop, ctx, ctx.locale) });
}

function laneClause(leaf: LaneLeaf, ctx: ReadsAsContext): string | null {
  const { t } = ctx;
  let summary: string;
  if (leaf.kind === 'perspective') summary = summarizePerspectiveFilter(leaf.filter, t, ctx.availablePerspectives);
  else if (leaf.kind === 'relationship') summary = summarizeRelationshipFilter(leaf.filter, t, ctx.availableRelationships);
  else summary = summarizeArcFilter(leaf.filter, t);
  // A lane row with nothing chosen yet reads as nothing, as an incomplete
  // property row does.
  if (!summary) return null;
  return t('filters.tree.readsAs.laneClause', { lane: t(`filters.lane.${getLaneForLeaf(leaf)}.badge`), summary });
}

function groupPart(group: FilterGroup, depth: number, ctx: ReadsAsContext): ReadsAsPart | null {
  if (depth >= MAX_EXPR_DEPTH) return { text: '…', compound: false };
  const parts: ReadsAsPart[] = [];
  for (const leaf of group.expressions ?? []) {
    const text = isPropertyLeaf(leaf) ? propertyClause(leaf, ctx) : laneClause(leaf, ctx);
    if (text) parts.push({ text, compound: false });
  }
  for (const child of group.groups ?? []) {
    const part = groupPart(child, depth + 1, ctx);
    if (part) parts.push(part);
  }
  if (parts.length === 0) return null;
  // A group holding one condition says nothing its condition doesn't — it
  // reads as that condition, unbracketed (advanced-filters-v2.jsx `afToPredicate`).
  if (parts.length === 1) return parts[0];
  const joiner = group.operator === 'or' ? ctx.t('filters.tree.readsAs.or') : ctx.t('filters.tree.readsAs.and');
  return {
    text: parts.map((p) => (p.compound ? `(${p.text})` : p.text)).join(` ${joiner} `),
    compound: true,
  };
}

/**
 * The predicate of a tree's "Reads as <subject> where …" sentence, in the
 * reader's words — "Urgency is none of Urgent, High AND (Arc: any arc · At Risk
 * OR Relationship: Blocks)". A property leaf reads through the aspect catalog's
 * label and its operator's localized clause; a lane leaf through its lane's
 * one-line summary; a nested group is bracketed. Incomplete rows and empty
 * groups are left out (they constrain nothing), so the result is `''` while no
 * condition in the tree is complete. Depth is capped at 8 ("…").
 */
export function readsAsPredicate(group: FilterGroup, ctx: ReadsAsContext): string {
  return groupPart(group, 0, ctx)?.text ?? '';
}
